import { Box, Skeleton, Tooltip, Typography } from "@mui/material";
import InfoPopover from "../../../Components/Common/poper/InfoPopover";
import DataTable from "../../../Components/Common/datatable/DataTable";
import { useEffect, useMemo, useRef, useState } from "react";
import TrafficLight from "../../BottlerForecast/Dashboard/TrafficLight";
import { useDispatch, useSelector } from "react-redux";
import ApiService from "../../../utils/fetch";
import { DateFormatterDDMMMYYYY } from "../../../Components/Common/DateFormatter";
import { useSupport } from "../../../context/SupportContext";
import RefreshIcon from "@mui/icons-material/Refresh";
import { BPtriggerRefresh } from "../../../redux/refresh";

function BPCurrentForecastTracker() {
  const [filteredData, setFilteredData] = useState([]);
  let { setbpCard1CombinationDetails } =
    useSupport();
  const [loading, setLoading] = useState(true);

  const gridRef = useRef();

  const finalTreeData = useSelector((state) => state.BPentity.BPfinalTreeData);
  const BPrefreshKey = useSelector((state) => state.refresh.BPrefreshKey);

  const dispatch = useDispatch();

  useEffect(() => {
    const loadCurrentForecastData = async () => {
      setLoading(true);
      try {
        const response = await ApiService.post(
          "/v1/business-plan/get-applicable-business-plan-status", //change after new API given
          finalTreeData,
        );
        setFilteredData(response.data);
      } catch (error) {
        console.error("Error loading current forecast data", error);
      } finally {
        setLoading(false);
      }
    };

    if (finalTreeData?.length > 0) {
      loadCurrentForecastData();
    } else {
      setFilteredData([]);
      setLoading(false);
    }
  }, [finalTreeData, BPrefreshKey]);

  function dateComparator(date1, date2) {
    const date1Number = _monthToNum(date1);
    const date2Number = _monthToNum(date2);

    if (date1Number === null && date2Number === null) {
      return 0;
    }
    if (date1Number === null) {
      return -1;
    }
    if (date2Number === null) {
      return 1;
    }

    return date1Number - date2Number;
  }

  function _monthToNum(date) {
    if (date === undefined || date === null || date.length !== 10) {
      return null;
    }

    var yearNumber = parseInt(date.substring(6, 10), 10);
    var monthNumber = parseInt(date.substring(3, 5), 10);
    var dayNumber = parseInt(date.substring(0, 2), 10);

    var result = yearNumber * 10000 + monthNumber * 100 + dayNumber;
    return result;
  }

  const StatusCellRenderer = (params) => {
    return (
      <div style={{ display: "flex", alignItems: "center" }}>
        <TrafficLight status={params.value} />
        <span>{params.value}</span>
      </div>
    );
  };

  const columnDefinition = useMemo(
    () => [
      {
        headerCheckboxSelection: true,
        checkboxSelection: true,
        field: "supportCheckbox",
        headerName: "",
        sortable: false,
        width: 50,
        suppressSizeToFit: true,
      },
      {
        field: "bottler",
        headerName: "Bottler",
        sortable: true,
        unSortIcon: true,
        filter: "agMultiColumnFilter",
        filterParams: {
          filters: [
            {
              filter: "agTextColumnFilter",
            },
            {
              filter: "agSetColumnFilter",
            },
          ],
        },
        width: 150,
        minWidth: 150,
        cellRenderer: (params) => (
          <Tooltip title={params.data.bottler || ""} arrow>
            <div>{params.data.bottler}</div>
          </Tooltip>
        ),
      },
      {
        field: "bottlerOwnerCode",
        headerName: "Bottler Owner Code",
        sortable: true,
        unSortIcon: true,
        filter: "agMultiColumnFilter",
        filterParams: {
          filters: [
            {
              filter: "agTextColumnFilter",
            },
            {
              filter: "agSetColumnFilter",
            },
          ],
        },
        width: 230,
        minWidth: 230,
        valueGetter: (params) => {
          const { bottlerOwnerCode,bottlerOwnerDescription } = params.data;
          return `${bottlerOwnerCode || ""}-${bottlerOwnerDescription || ""}`;
        },
        cellRenderer: (params) => {
          const display = params.value;
          return (
            <Tooltip title={<span>{display}</span>} arrow>
              <div>{display}</div>
            </Tooltip>
          );
        },
      },
      {
        field: "profitCenterCountry",
        headerName: "Customer Country",
        sortable: true,
        unSortIcon: true,
        suppressHeaderMenuButton: true,
        filter: "agMultiColumnFilter",
        filterParams: {
          filters: [
            {
              filter: "agTextColumnFilter",
            },
            {
              filter: "agSetColumnFilter",
            },
          ],
        },
        width: 200,
        minWidth: 200,
        cellRenderer: (params) => (
          <Tooltip title={params.data.profitCenterCountry || ""} arrow>
            <div>{params.data.profitCenterCountry}</div>
          </Tooltip>
        ),
      },
      {
        field: "fiscalmonth",
        headerName: "Fiscal Period",
        sortable: true,
        unSortIcon: true,
        suppressHeaderMenuButton: true,
        filter: "agMultiColumnFilter",
        filterParams: {
          filters: [
            {
              filter: "agTextColumnFilter",
            },
            {
              filter: "agSetColumnFilter",
            },
          ],
        },
        width: 155,
        minWidth: 155,
        cellRenderer: (params) => <div>{params.data.fiscalmonth}</div>,
      },
      {
        field: "status",
        headerName: "Status",
        sortable: true,
        unSortIcon: true,
        suppressHeaderMenuButton: true,
        filter: "agMultiColumnFilter",
        filterParams: {
          filters: [
            {
              filter: "agTextColumnFilter",
            },
            {
              filter: "agSetColumnFilter",
            },
          ],
        },
        width: 180,
        minWidth: 180,
        cellRenderer: StatusCellRenderer,
      },
    ],
    [],
  );

  const onGrid = (params) => {
    const gridApi = params.api;
    if (gridApi) {
      gridApi.sizeColumnsToFit();

      // Adding a resize event listener to handle zoom changes
      window.addEventListener("resize", () => {
        gridApi.sizeColumnsToFit();
        gridApi.refreshCells();
      });
    } else {
      console.error("Grid API is not available.");
    }
  };

  const pagination = true;
  const paginationPageSize = 10;
  const paginationPageSizeSelector = [5, 10, 20, 50, 100];

  const handleSelectionChanged = (event) => {
    const selectedRows = event.api.getSelectedRows();
    const combinationDetails = selectedRows.map(
      ({ bottler, profitCenterCountry, bottlerOwnerCode }) => ({
        bottler,
        profitCenterCountry,
        bottlerOwnerCode,
      }),
    );

    setbpCard1CombinationDetails(combinationDetails);
  };

  return (
    <>
      <div className="container">
        <Box>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <Typography variant="subtitle1" align="left" gutterBottom="true">
                Business Plan Upload Status
              </Typography>
              <InfoPopover margin="0 0 3px 0" />
            </Box>
            <Tooltip title="Refresh" arrow placement="right">
              <RefreshIcon
                sx={{
                  cursor: "pointer",
                  color: "grey",
                  "&:hover": {
                    color: "grey",
                  },
                }}
                onClick={() => {
                  dispatch(BPtriggerRefresh());
                }}
              />
            </Tooltip>
          </Box>
          {loading ? (
            <Skeleton
              variant="rectangular"
              height={280}
              animation="pulse"
              style={{ marginBottom: "16px", borderRadius: "8px" }}
            />
          ) : (
            <DataTable
              ref={gridRef}
              columns={columnDefinition}
              rows={filteredData || []}
              pagination={pagination}
              paginationPageSize={paginationPageSize}
              paginationPageSizeSelector={paginationPageSizeSelector}
              suppressPaginationPanel={false}
              tableHeight={"50vh"}
              suppressMovableColumns={true}
              suppressHorizontalScroll={false}
              onGridReady={onGrid}
              suppressHeaderContextMenu={true}
              onSelectionChanged={handleSelectionChanged}
            />
          )}
        </Box>
      </div>
    </>
  );
}

export default BPCurrentForecastTracker;
